import React from "react";
import moment from "moment";
import { mdiCellphoneMarker, mdiCircleOutline, mdiCircleSlice8, mdiHomeMapMarker } from "@mdi/js";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { minutesText, secondsText, sunText } from "../../../automation/format";
import { coordinatesText } from "../HomeLocationSheet";
import { sensorEvents } from "./draft";
import { Card, Chips, DayCircles, Hint, Label, OptionRow, Segmented, Stepper, TimeBox } from "./controls";

// Step 1 of the create flow, what starts it and its details
// (app/screens/Automation/create/StartSteps.js).

// The circle a phone watches round the home (app/automation/presence.js RADIUS_METERS)
const RADIUS_METERS = 200;

// What can start it. Arriving or leaving runs off the phone's location, and an
// NFC tag is written with a phone, so new ones are set up in the app.
const TRIGGERS = [
  { type: "tap", title: "Tap to run", text: "Example: turn on all the lights in the hall with one tap." },
  { type: "schedule", title: "Time", text: "Example: geyser on at 6:30 AM on weekdays." },
  { type: "timer", title: "Countdown", text: "Example: fan off in 2 hours." },
  { type: "sun", title: "Sunrise/Sunset", text: "Example: lights on 15 minutes before sunset." },
  { type: "rule", title: "A device event", text: "Example: when the main door opens at night, turn on the hall light." },
  {
    type: "location",
    title: "Arriving or leaving home",
    text: "It runs off your phone's location, so set it up in the HASHh Connect app.",
    badge: "In the app",
    disabled: true,
  },
  {
    type: "nfc",
    title: "NFC tag",
    text: "Tags are written with a phone, so set it up in the HASHh Connect app.",
    badge: "In the app",
    disabled: true,
  },
];

export const TriggerStep = ({ ready, sensors, onPick }) => (
  <div className="hx-auto-options">
    {TRIGGERS.filter((option) => ready(option.type)).map((option) => {
      const noSensor = option.type === "rule" && !sensors.length;
      return (
        <OptionRow
          key={option.type}
          type={option.type}
          title={option.title}
          text={noSensor ? "Add a door, motion, gas or smoke sensor to use this." : option.text}
          badge={option.badge}
          disabled={option.disabled || noSensor}
          onPress={() => onPick(option.type)}
        />
      );
    })}
  </div>
);

const ScheduleDetails = ({ trigger, onChange }) => (
  <>
    <Card>
      <Label>At</Label>
      <TimeBox value={trigger.time} onChange={(time) => onChange({ ...trigger, time })} label="Time" />
    </Card>
    <Card>
      <Label>Repeat on</Label>
      <DayCircles days={trigger.days} onChange={(days) => onChange({ ...trigger, days })} label="Repeat on" />
    </Card>
  </>
);

const offsetLabel = (minutes) => (minutes === 0 ? "At the time" : `${minutesText(minutes)} ${minutes < 0 ? "before" : "after"}`);

// The home's location, with Set or Change (HomeLocationSheet). The web has no
// geocoder to name the place, so it shows the coordinates.
const HomeLocationCard = ({ home, onSetLocation, hint }) => {
  const location = home?.location;
  return (
    <Card>
      <div className="hx-auto-location">
        <MdiIcon path={mdiHomeMapMarker} size={22} />
        <div>
          <Label>Home location</Label>
          <p>{location ? coordinatesText(location) : "Not set yet"}</p>
        </div>
        <button
          type="button"
          className="hx-auto-link"
          onClick={onSetLocation}
          aria-label={location ? "Change the home's location" : "Set the home's location"}
        >
          {location ? "Change" : "Set"}
        </button>
      </div>
      <Hint>{hint}</Hint>
    </Card>
  );
};

const SunDetails = ({ trigger, onChange, home, onSetLocation }) => (
  <>
    <Card>
      <Label>When</Label>
      <Segmented
        label="Sunrise or sunset"
        options={[
          { key: "sunrise", label: "Sunrise" },
          { key: "sunset", label: "Sunset" },
        ]}
        value={trigger.event}
        onChange={(event) => onChange({ ...trigger, event })}
      />
      <div className="hx-auto-offset">
        <label className="hx-auto-step-label" htmlFor="hx-auto-offset">
          Offset
        </label>
        <span>{sunText(trigger)}</span>
      </div>
      <input
        id="hx-auto-offset"
        type="range"
        className="hx-auto-range"
        min={-120}
        max={120}
        step={5}
        value={trigger.offsetMinutes}
        aria-valuetext={sunText(trigger)}
        onChange={(event) => onChange({ ...trigger, offsetMinutes: Math.round(Number(event.target.value)) })}
      />
      <div className="hx-auto-offset-scale" aria-hidden="true">
        <span>−2 h</span>
        <span>{offsetLabel(0)}</span>
        <span>+2 h</span>
      </div>
    </Card>
    <Card>
      <Label>Repeat on</Label>
      <DayCircles days={trigger.days} onChange={(days) => onChange({ ...trigger, days })} label="Repeat on" />
    </Card>
    <HomeLocationCard home={home} onSetLocation={onSetLocation} hint="Sunrise and sunset times come from your home's location." />
  </>
);

// Each phone reports only its own person, so it's always "you". Only one made
// in the app gets here: the phone that watches the home is shown there.
const LocationDetails = ({ trigger, onChange, home, onSetLocation }) => (
  <>
    <Card>
      <Label>When you</Label>
      <Segmented
        label="Arrive or leave"
        options={[
          { key: "arrive", label: "Arrive home" },
          { key: "leave", label: "Leave home" },
        ]}
        value={trigger.event}
        onChange={(event) => onChange({ ...trigger, event })}
      />
      <Hint>{`Your phone notices when you come within ${RADIUS_METERS} m of home or go further. It can take a minute or two.`}</Hint>
    </Card>
    <HomeLocationCard home={home} onSetLocation={onSetLocation} hint={`Your phone watches a ${RADIUS_METERS} m circle round this spot.`} />
    <Card>
      <div className="hx-auto-location">
        <MdiIcon path={mdiCellphoneMarker} size={22} />
        <div>
          <Label>Your phone</Label>
          <p>The HASHh Connect app on your phone tells HASHh when you arrive or leave. Check it there.</p>
        </div>
      </div>
    </Card>
  </>
);

const DELAYS = [15, 30, 60, 120, 240, 480];

const TimerDetails = ({ trigger, onChange }) => {
  const minutes = trigger.delayMinutes || 0;
  const set = (delayMinutes) => onChange({ ...trigger, delayMinutes });
  return (
    <Card>
      <Label>Run it once, in</Label>
      <Chips
        label="Run it once, in"
        options={DELAYS.map((value) => ({ key: value, label: minutesText(value) }))}
        value={DELAYS.includes(minutes) ? minutes : null}
        onChange={set}
      />
      <div className="hx-auto-step-row">
        <Hint>Hours</Hint>
        <Stepper label="Hours" value={Math.floor(minutes / 60)} min={0} max={167} onChange={(h) => set(h * 60 + (minutes % 60))} />
      </div>
      <div className="hx-auto-step-row">
        <Hint>Minutes</Hint>
        <Stepper label="Minutes" value={minutes % 60} min={0} max={55} step={5} onChange={(m) => set(Math.floor(minutes / 60) * 60 + m)} />
      </div>
      {minutes > 0 ? (
        <Hint>Runs at {moment().add(minutes, "minutes").format("h:mm A, ddd D MMM")} (counted from when you save).</Hint>
      ) : null}
    </Card>
  );
};

const RuleDetails = ({ trigger, onChange, sensors, lookup }) => {
  const events = sensorEvents(lookup.device(trigger.applianceId));
  const pickSensor = (applianceId) => {
    const first = sensorEvents(lookup.device(applianceId))[0];
    onChange({ ...trigger, applianceId, fact: first?.fact || null, value: first ? first.value : null });
  };
  const eventKey = events.find((e) => e.fact === trigger.fact && e.value === trigger.value)?.key;
  return (
    <>
      <Card>
        <Label id="hx-auto-sensor">When this sensor…</Label>
        <div role="radiogroup" aria-labelledby="hx-auto-sensor">
          {sensors.map((sensor) => {
            const selected = sensor.applianceId === trigger.applianceId;
            return (
              <button
                key={sensor.applianceId}
                type="button"
                role="radio"
                aria-checked={selected}
                className="hx-auto-pick-row"
                onClick={() => pickSensor(sensor.applianceId)}
              >
                <MdiIcon path={selected ? mdiCircleSlice8 : mdiCircleOutline} size={22} />
                <span>
                  {sensor.name}
                  {sensor.room ? <small> {sensor.room}</small> : null}
                </span>
              </button>
            );
          })}
        </div>
      </Card>
      {events.length ? (
        <Card>
          <Label>…does this</Label>
          <Segmented
            label="What it does"
            options={events.map((e) => ({ key: e.key, label: e.label }))}
            value={eventKey}
            onChange={(key) => {
              const event = events.find((e) => e.key === key);
              onChange({ ...trigger, fact: event.fact, value: event.value });
            }}
          />
        </Card>
      ) : null}
    </>
  );
};

const INCHING_PRESETS = [10, 30, 60, 300, 900, 1800, 3600];

const InchingDetails = ({ trigger, onChange, deviceName }) => {
  const seconds = trigger.seconds || 0;
  const set = (value) => onChange({ ...trigger, seconds: Math.max(1, Math.min(86400, value)) });
  return (
    <Card>
      <Label>Switch {deviceName} off after it turns on, in</Label>
      <Chips
        label="Switch off after"
        options={INCHING_PRESETS.map((value) => ({ key: value, label: secondsText(value) }))}
        value={INCHING_PRESETS.includes(seconds) ? seconds : null}
        onChange={set}
      />
      <div className="hx-auto-step-row">
        <Hint>Minutes</Hint>
        <Stepper label="Minutes" value={Math.floor(seconds / 60)} min={0} max={1440} onChange={(m) => set(m * 60 + (seconds % 60))} />
      </div>
      <div className="hx-auto-step-row">
        <Hint>Seconds</Hint>
        <Stepper label="Seconds" value={seconds % 60} min={0} max={55} step={5} onChange={(s) => set(Math.floor(seconds / 60) * 60 + s)} />
      </div>
      <Hint>Runs every time it's switched on, from the app, a schedule or the wall switch.</Hint>
    </Card>
  );
};

const CYCLE_PRESETS = [5, 10, 15, 30, 60];

const CyclingDetails = ({ trigger, onChange, deviceName }) => {
  const set = (key, value) => onChange({ ...trigger, [key]: Math.max(1, Math.min(1440, value)) });
  return (
    <Card>
      <Label>Keep {deviceName} on and off in turns</Label>
      <Hint>On for</Hint>
      <Chips
        label="On for"
        options={CYCLE_PRESETS.map((value) => ({ key: value, label: minutesText(value) }))}
        value={trigger.onMinutes}
        onChange={(value) => set("onMinutes", value)}
      />
      <div className="hx-auto-step-row">
        <Hint>Minutes on</Hint>
        <Stepper label="Minutes on" value={trigger.onMinutes} min={1} max={1440} onChange={(value) => set("onMinutes", value)} />
      </div>
      <Hint>Then off for</Hint>
      <Chips
        label="Then off for"
        options={CYCLE_PRESETS.map((value) => ({ key: value, label: minutesText(value) }))}
        value={trigger.offMinutes}
        onChange={(value) => set("offMinutes", value)}
      />
      <div className="hx-auto-step-row">
        <Hint>Minutes off</Hint>
        <Stepper label="Minutes off" value={trigger.offMinutes} min={1} max={1440} onChange={(value) => set("offMinutes", value)} />
      </div>
    </Card>
  );
};

// Step 1, the details of what starts it
export function DetailsStep({ draft, onTrigger, home, onSetLocation, sensors, lookup }) {
  const props = { trigger: draft.trigger, onChange: onTrigger };
  switch (draft.type) {
    case "schedule":
      return <ScheduleDetails {...props} />;
    case "sun":
      return <SunDetails {...props} home={home} onSetLocation={onSetLocation} />;
    case "location":
      return <LocationDetails {...props} home={home} onSetLocation={onSetLocation} />;
    case "timer":
      return <TimerDetails {...props} />;
    case "rule":
      return <RuleDetails {...props} sensors={sensors} lookup={lookup} />;
    case "inching":
      return <InchingDetails {...props} deviceName={lookup.name(draft.trigger.applianceId)} />;
    case "cycling":
      return <CyclingDetails {...props} deviceName={lookup.name(draft.trigger.applianceId)} />;
    default:
      return null;
  }
}
